import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Calendar as CalendarIcon, Layers, Loader2, Play } from "lucide-react";
import { useToast } from "../../../contexts/ToastContext";
import { api } from "../../../services/api";
import { ConfirmationDialog } from "../../ui/confirmation-dialog";

const CUSTOM_COMPONENTS = [
  { id: 'summary', name: 'Broker Summary' },
  { id: 'transaction', name: 'Broker Transaction' },
  { id: 'stock', name: 'Broker Transaction Stock' },
  { id: 'idx', name: 'Broker Summary IDX' },
  { id: 'sector', name: 'Broker Summary Sector' },
  { id: 'all', name: 'Broker Transaction ALL' },
  { id: 'stock-sector-idx-all', name: 'Broker Transaction Stock Sector/IDX ALL' },
  { id: 'granular', name: 'Broker Summary Granular' },
];

export function CustomDataTrigger() {
  const { showToast } = useToast();
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedCustomComponents, setSelectedCustomComponents] = useState<string[]>(['summary', 'transaction', 'stock', 'idx', 'sector', 'all', 'stock-sector-idx-all', 'granular']);
  const [customTriggering, setCustomTriggering] = useState(false);
  
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => { },
  });

  const handleTriggerCustomRange = async () => {
    if (!customStartDate || !customEndDate) {
      showToast({ type: 'error', title: 'Error', message: 'Start and End dates are required' });
      return;
    }
    
    if (selectedCustomComponents.length === 0) {
      showToast({ type: 'error', title: 'Error', message: 'Please select at least one component' });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: 'Trigger Custom Range',
      description: `Are you sure you want to trigger data generation for range ${customStartDate} to ${customEndDate} for ${selectedCustomComponents.length} components?`,
      onConfirm: async () => {
        setCustomTriggering(true);
        setConfirmationDialog(prev => ({ ...prev, open: false }));
        try {
          // Format dates to YYYYMMDD for backend
          const formattedStart = customStartDate.replace(/-/g, '');
          const formattedEnd = customEndDate.replace(/-/g, '');

          const result = await api.triggerBrokerRange({
            startDate: formattedStart,
            endDate: formattedEnd,
            components: selectedCustomComponents,
            force: true
          });

          if (result.success) {
            showToast({ type: 'success', title: 'Success', message: 'Custom range trigger started successfully (check Manual Trigger Progress)' });
          } else {
            showToast({ type: 'error', title: 'Error', message: result.error || 'Failed to trigger custom range' });
          }
        } catch (error: any) {
          showToast({ type: 'error', title: 'Error', message: error.message || 'Error triggering custom range' });
        } finally {
          setCustomTriggering(false);
        }
      }
    });
  };

  const toggleComponent = (id: string) => {
    setSelectedCustomComponents(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const selectAllComponents = () => {
    setSelectedCustomComponents(CUSTOM_COMPONENTS.map(c => c.id));
  };

  const clearComponentsSelection = () => {
    setSelectedCustomComponents([]);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            <span className="text-lg sm:text-xl">Custom Data Trigger</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date Range Selection */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Date Range
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date" className="text-xs">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date" className="text-xs">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Component Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Components to Process
                  </h4>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllComponents} className="h-6 text-[10px] px-2">Select All</Button>
                    <Button variant="ghost" size="sm" onClick={clearComponentsSelection} className="h-6 text-[10px] px-2">Clear</Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-md p-3 max-h-[140px] overflow-y-auto">
                  {CUSTOM_COMPONENTS.map((comp) => (
                    <div key={comp.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`comp-${comp.id}`} 
                        checked={selectedCustomComponents.includes(comp.id)}
                        onCheckedChange={() => toggleComponent(comp.id)}
                      />
                      <Label 
                        htmlFor={`comp-${comp.id}`}
                        className="text-xs cursor-pointer truncate"
                      >
                        {comp.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-2 border-t flex justify-end">
              <Button 
                onClick={handleTriggerCustomRange} 
                disabled={customTriggering}
                className="w-full sm:w-auto"
              >
                {customTriggering ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {customTriggering ? 'Triggering...' : 'Trigger Custom Range'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onOpenChange={(open) => setConfirmationDialog({ ...confirmationDialog, open })}
        title={confirmationDialog.title}
        description={confirmationDialog.description}
        onConfirm={confirmationDialog.onConfirm}
        confirmText="Yes, Trigger"
        cancelText="Cancel"
      />
    </>
  );
}
